// [biometric-login-2026-10-11] FACE ID / TOUCH ID SIGN-IN — shared module.
//
// One source for all four StrikeServe iOS bundles:
//   - Process Server  (this repo, imported by src/server-app.jsx)
//   - Client / Agency (strikeserve-mobile src/*.jsx import it from vendor/server-web)
//   - Admin           (strikeserve-mobile build.sh bundles it to www/ss-biometric.js,
//                      exposed as window.SSBiometric for the hand-written admin.js)
//
// How it works
//   The native StrikeServeBiometric plugin (strikeserve-mobile
//   ios/App/App/BiometricPlugin.swift) keeps the account's REFRESH token in the
//   iOS Keychain, protected so only the enrolled face/finger can release it
//   (.biometryCurrentSet: re-enrolling Face ID or adding a fingerprint
//   invalidates it). Signing in with Face ID = release that token, trade it at
//   POST /api/auth/refresh for a fresh token pair, store the rotated refresh
//   token back (no prompt), then the caller loads the user with /api/auth/me.
//   No backend change: /api/auth/refresh and /api/auth/login already return
//   refreshToken in the JSON body.
//   [biometric-login-v2-2026-10-18] Backend gates: /api/auth/refresh applies the
//   same approval + wrong-portal gates as /api/auth/login (cle-backend
//   feat/biometric-login-v2-2026-10-18), so a Face ID sign-in can no longer
//   bypass a rejected/suspended/revoked status. /api/auth/me then returns
//   applicationStatus, and onLogin routes a still-onboarding server to the
//   application screen exactly as after a password sign-in.
//
// On the web (no native shell) every function is a safe no-op: bioAvailable()
// resolves { available:false } and the UI never shows.

const PLUGIN = "StrikeServeBiometric";
const LS_EMAIL = (role) => "ss_bio_email_" + role;     // non-secret: which account is enrolled
const LS_ASKED = (role) => "ss_bio_asked_" + role;     // user answered the enroll prompt
/* [recon-server-web-2026-10-21] set when the stored token came back expired; suppresses the
   automatic Face ID prompt on launch until the next password sign-in re-enrolls. */
const LS_EXPIRED = (role) => "ss_bio_expired_" + role;

function plugin() {
  try {
    const c = typeof window !== "undefined" ? window.Capacitor : null;
    if (!c || typeof c.isNativePlatform !== "function" || !c.isNativePlatform()) return null;
    return (c.Plugins && c.Plugins[PLUGIN]) || null;
  } catch (_) { return null; }
}
function lsGet(k) { try { return localStorage.getItem(k) || ""; } catch (_) { return ""; } }
function lsSet(k, v) { try { if (v) localStorage.setItem(k, v); else localStorage.removeItem(k); } catch (_) {} }

/* { available, type: "faceID"|"touchID"|"opticID"|"none", label: "Face ID"|"Touch ID"|... } */
export async function bioAvailable() {
  const p = plugin();
  if (!p) return { available: false, type: "none", label: "Face ID" };
  try {
    const r = await p.isAvailable();
    const type = (r && r.biometryType) || "none";
    const label = type === "touchID" ? "Touch ID" : type === "opticID" ? "Optic ID" : "Face ID";
    return { available: !!(r && r.available), type, label };
  } catch (_) { return { available: false, type: "none", label: "Face ID" }; }
}

/* Is a credential stored for this role? Never prompts. */
export async function bioEnrolled(role) {
  const p = plugin();
  if (!p) return false;
  try { const r = await p.has({ account: role }); return !!(r && r.exists); }
  catch (_) { return false; }
}

export function bioEnrolledEmail(role) { return lsGet(LS_EMAIL(role)); }
export function bioWasAsked(role) { return lsGet(LS_ASKED(role)) === "1"; }
export function bioMarkAsked(role) { lsSet(LS_ASKED(role), "1"); }
/* [recon-server-web-2026-10-21] true after an "expired" Face ID sign-in, until a password sign-in. */
export function bioIsExpired(role) { return lsGet(LS_EXPIRED(role)) === "1"; }

/* Store the refresh token behind Face ID. No prompt (iOS only prompts on read). */
export async function bioEnroll(role, email, refreshToken) {
  const p = plugin();
  if (!p || !refreshToken) return false;
  try {
    await p.save({ account: role, secret: String(refreshToken) });
    lsSet(LS_EMAIL(role), String(email || "").toLowerCase());
    lsSet(LS_EXPIRED(role), ""); /* [recon-server-web-2026-10-21] fresh token: auto-prompt again */
    bioMarkAsked(role);
    return true;
  } catch (_) { return false; }
}

/* After any password sign-in: if this role is already enrolled for the SAME
   account, silently replace the stored token with the new one (keeps Face ID
   working past the refresh token's lifetime). A different account signing in
   turns Face ID off for this role rather than leaving the old account's token.
   [recon-server-web-2026-10-21] An unknown enrolled email (localStorage cleared while the
   Keychain item survived) counts as a mismatch: Face ID is turned off, never silently
   re-enrolled for whoever just signed in. The account check runs even with no refreshToken
   (a "Remember me"-off sign-in passes ""), which then only keeps or drops the enrollment. */
export async function bioRefreshAfterPasswordLogin(role, email, refreshToken) {
  if (!plugin()) return;
  if (!(await bioEnrolled(role))) return;
  const enrolledEmail = bioEnrolledEmail(role);
  if (!enrolledEmail || enrolledEmail !== String(email || "").toLowerCase()) { await bioDisable(role); return; }
  if (!refreshToken) return;
  await bioEnroll(role, email, refreshToken);
}

export async function bioDisable(role) {
  const p = plugin();
  lsSet(LS_EMAIL(role), "");
  lsSet(LS_EXPIRED(role), ""); /* [recon-server-web-2026-10-21] */
  if (!p) return;
  try { await p.remove({ account: role }); } catch (_) {}
}

/* Face ID → fresh session. Resolves { token, refreshToken } or throws an Error
   whose .code is "cancelled" | "not_enrolled" | "expired" | "blocked" | "failed".
   [biometric-login-v2-2026-10-18] "blocked" = a backend sign-in gate refused the account. */
/* [recon-server-web-2026-10-21] appName: the current tenant/brand display name for the iOS
   prompt ("Sign in to <appName>"); falls back to "StrikeServe". */
export async function bioSignIn(role, apiBase, label, appName) {
  const p = plugin();
  const fail = (code, msg) => { const e = new Error(msg); e.code = code; return e; };
  if (!p) throw fail("not_enrolled", "Face ID is not available on this device");
  let secret;
  try {
    const _name = String(appName || "").trim() || "StrikeServe";
    const r = await p.load({ account: role, reason: "Sign in to " + _name });
    secret = r && r.secret;
  } catch (e) {
    const code = (e && (e.code || e.message)) || "";
    if (/cancel/i.test(code)) throw fail("cancelled", "Cancelled");
    if (/not_found|notfound|invalid/i.test(code)) {
      await bioDisable(role);
      throw fail("not_enrolled", (label || "Face ID") + " sign-in was reset on this iPhone. Sign in with your password to turn it back on.");
    }
    throw fail("failed", (label || "Face ID") + " did not recognize you. Sign in with your password.");
  }
  if (!secret) throw fail("not_enrolled", "Sign in with your password to set up " + (label || "Face ID") + ".");

  let res, body = {};
  try {
    res = await fetch(String(apiBase || "") + "/api/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: secret }),
    });
    body = await res.json().catch(() => ({}));
  } catch (_) {
    throw fail("failed", "No connection. Check your signal and try again.");
  }
  if (!res.ok || !body || !body.token) {
    /* [biometric-login-v2-2026-10-18] POST /api/auth/refresh now runs the same sign-in gates as
       password login (approval/suspended/revoked/disabled/wrong portal). Show that refusal as-is
       (backend message carries the "(Error code: X)" suffix) and drop the stored token, so a
       blocked account cannot keep retrying with it. SESSION_EXPIRED keeps the old path below. */
    const gateCode = body && body.code;
    if (res.status === 403 && gateCode && gateCode !== "SESSION_EXPIRED") {
      await bioDisable(role);
      throw fail("blocked", (body && (body.error || body.message)) || "This account cannot sign in. Contact support.");
    }
    if (res.status === 401 || res.status === 403) {
      // Token expired/revoked server-side. Keep enrollment; the next password
      // sign-in stores a fresh token automatically (bioRefreshAfterPasswordLogin).
      lsSet(LS_EXPIRED(role), "1"); /* [recon-server-web-2026-10-21] no auto-prompt until then */
      throw fail("expired", "Your " + (label || "Face ID") + " sign-in has expired. Sign in with your password once to renew it.");
    }
    throw fail("failed", (body && body.error) || "Sign-in failed. Try again.");
  }
  if (body.refreshToken) {
    try { await p.save({ account: role, secret: String(body.refreshToken) }); } catch (_) {}
  }
  return { token: body.token, refreshToken: body.refreshToken || "" };
}
